import React from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Cookie,
  Settings2,
  FileText,
  AlertTriangle,
  Building2,
  Lock,
  Accessibility,
  ArrowUpRight,
} from "lucide-react";
import { PageHero, SectionWrapper } from "../components/Section";

const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent("allinstra:open-cookie-settings"));
};

const LEGAL_LINKS = [
  {
    icon: Shield,
    title: "Privacy Policy",
    desc: "How Allinstra handles personal information collected through this website.",
    to: "/privacy",
    testId: "legal-center-privacy",
  },
  {
    icon: Cookie,
    title: "Cookie Policy",
    desc: "Information about the cookies and similar technologies used on this website.",
    to: "/cookie-policy",
    testId: "legal-center-cookie-policy",
  },
  {
    icon: Settings2,
    title: "Cookie Settings",
    desc: "Review or update your current cookie preferences for this website.",
    action: "cookie-settings",
    testId: "legal-center-cookie-settings",
  },
  {
    icon: FileText,
    title: "Terms of Use",
    desc: "Conditions that apply to your use of the Allinstra website.",
    to: "/terms",
    testId: "legal-center-terms",
  },
  {
    icon: AlertTriangle,
    title: "Disclaimer",
    desc: "Scope, limitations, and informational nature of the website content.",
    to: "/disclaimer",
    testId: "legal-center-disclaimer",
  },
  {
    icon: Building2,
    title: "Company Information / Legal Notice",
    desc: "Imprint and legal entity information for the operator of this website.",
    to: "/company-information",
    testId: "legal-center-company-information",
  },
  {
    icon: Lock,
    title: "Security",
    desc: "Security principles and responsible disclosure information.",
    to: "/security",
    testId: "legal-center-security",
  },
  {
    icon: Accessibility,
    title: "Accessibility",
    desc: "Allinstra’s commitment to accessible digital experiences.",
    to: "/accessibility",
    testId: "legal-center-accessibility",
  },
];

const LegalCard = ({ item }) => {
  const Inner = (
    <>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
          <item.icon size={18} strokeWidth={1.5} />
        </div>
        <ArrowUpRight
          size={16}
          className="text-ink-400 group-hover:text-cyan-600 transition-colors"
        />
      </div>
      <h3 className="font-display text-lg sm:text-xl font-semibold text-ink-900 tracking-tight mt-5 leading-snug">
        {item.title}
      </h3>
      <p className="mt-3 text-[14.5px] leading-relaxed text-ink-600">{item.desc}</p>
    </>
  );

  const sharedClasses =
    "group bg-white border border-ink-200 p-7 sm:p-8 card-hover flex flex-col text-left w-full";

  if (item.action === "cookie-settings") {
    return (
      <button
        type="button"
        onClick={openCookieSettings}
        data-testid={item.testId}
        className={sharedClasses}
      >
        {Inner}
      </button>
    );
  }

  return (
    <Link to={item.to} data-testid={item.testId} className={sharedClasses}>
      {Inner}
    </Link>
  );
};

const LegalCenter = () => (
  <main data-testid="page-legal-center">
    <PageHero
      overline="Legal Center"
      title="Legal, privacy and website information."
      lead="This page provides access to Allinstra’s website legal information, privacy notices, cookie information, company information, security notice, and accessibility statement."
    />

    <SectionWrapper id="legal-center-grid">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {LEGAL_LINKS.map((item) => (
          <LegalCard key={item.testId} item={item} />
        ))}
      </div>
      <p className="mt-12 text-[13px] text-ink-500 max-w-3xl leading-relaxed">
        Allinstra is an institutional systems brand by Erenown. Documents linked from this
        page describe the operation of the Allinstra website and are reviewed periodically.
      </p>
    </SectionWrapper>
  </main>
);

export default LegalCenter;
